import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  resource,
  signal,
} from '@angular/core';
import { CtBatchStoreService } from '../../service/app-state/ct-batch-store.service';
import { FieldGroupExtractionViewComponent } from '../../domains/data-capturing/field-group-extraction-view-component/field-group-extraction-view.component';
import { CtDocument } from '@ps-gui/claims-terminal-shared';
import {
  ExtractionPage,
  ExtractionSection,
  isFieldGroupModel,
  isTableModel,
} from '../../models/domain-extraction-specifications';
import { TableExtractionViewComponent } from '../../domains/data-capturing/table-extraction-view-component/table-extraction-view.component';
import { DiscardTableDialogComponent } from '../../domains/dialogs/discard-table-dialog/discard-table-dialog.component';
import { TableOverlayStoreService } from '../../service/overlay/table-overlay-store.service';
import { FieldModel } from '../../models/extraction-models/extraction-models';
import { FieldFormControl } from '../../models/extraction-form-models';
import { ExtractedRow, ExtractedValue, ExtractedValuesByKey } from '../../models/extracted-values';
import { PageNavigationService } from '../../service/app-state/page-navigation.service';
import { FormControlStatus } from '@angular/forms';
import { DocumentBasedInitialDataProvider } from '../../domains/data-capturing/extraction-data-provider';
import { ExtractedDataStoreService } from '../../service/extracted-data/extracted-data-store.service';
import { OcrFormInputDirective } from '../../directives/ocr-form-input.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EseFormValidator } from './model/ese-form-validator.model';
import { PrefillResults } from '../../service/prefill.service';

export interface ExtractionField {
  model: FieldModel;
  idOverride?: string;
  control?: FieldFormControl;
}

@Component({
  selector: 'ct-extraction-page',
  imports: [
    FieldGroupExtractionViewComponent,
    TableExtractionViewComponent,
    DiscardTableDialogComponent,
    OcrFormInputDirective,
  ],
  templateUrl: './extraction-page.component.html',
})
export class ExtractionPageComponent {
  protected readonly ctBatchStoreService = inject(CtBatchStoreService);
  private readonly tableOverlayStoreService = inject(TableOverlayStoreService);
  private readonly pageNavigationService = inject(PageNavigationService);
  private readonly initialDataProvider = inject(DocumentBasedInitialDataProvider);
  private readonly extractedDataStoreService = inject(ExtractedDataStoreService);

  readonly currentDocument = input.required<CtDocument>();
  readonly extractionPage = input.required<ExtractionPage>();
  readonly currentActiveField = model.required<ExtractionField | undefined>();
  readonly currentActiveSection = model.required<ExtractionSection | undefined>();
  readonly currentActiveFieldModel = input.required<
    FieldModel<string, ExtractedValue> | undefined
  >();
  readonly areAllSectionsValid = input.required<boolean>();
  readonly hasMultipleTableSections = input(false);

  readonly formFieldMap = signal<Record<string, ElementRef>>({});

  back = output();
  continue = output();

  focusNextSection = output();
  changeSectionStatus = output<{ section: ExtractionSection; status: FormControlStatus }>();

  private readonly currentDocumentId = computed(() => this.currentDocument().id);

  protected readonly initialFieldDataBySectionKey = computed<PrefillResults>(() => {
    // TODO: Right now this implementation is a hack to say we want to update the initial data
    //  to update when the page changes. Reevaluate how we can make this cleaner.
    //  Another Idea was to provide a service to the child which then loads the initial data, but this does
    //  not work easily because the service would need to access the inputs of the providing component...
    this.extractionPage();
    return {
      fields: this.initialDataProvider.getInitialData(this.currentDocumentId()),
      sources: {},
    };
  });

  protected readonly initialTableRowsByKeyResource = resource({
    params: () => ({ documentId: this.currentDocumentId() }),
    loader: async ({ params: { documentId } }) =>
      await this.extractedDataStoreService.getExtractedTables(documentId),
  });

  private readonly eseAuftragsformValidationActive = computed(() =>
    this.currentDocument().documentClasses.includes('25_Q3_AUFTRAGSFORMULAR_ESE'),
  );

  constructor() {
    effect(() => {
      const formFieldMap = this.formFieldMap();
      const currentActiveField = this.currentActiveField();
      this.scrollToCurrentActiveField(currentActiveField, formFieldMap);
    });

    effect(() => {
      if (this.eseAuftragsformValidationActive()) {
        return;
      }

      if (this.areAllSectionsValid()) {
        this.pageNavigationService.enableNavigation('continue');
      } else {
        this.pageNavigationService.disableNavigation(
          'continue',
          'Bitte vervollständige bzw. korrigiere deine Eingaben',
        );
      }
    });

    this.pageNavigationService
      .navigate$('continue')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.continue.emit());
  }

  discardAndGoToPrevView() {
    this.tableOverlayStoreService.deleteUnconfirmedTables();
    this.back.emit();
  }

  protected readonly isFieldGroupModel = isFieldGroupModel;
  protected readonly isTableModel = isTableModel;

  protected onSectionStatusChange(section: ExtractionSection, status: FormControlStatus) {
    this.changeSectionStatus.emit({ section, status });
  }

  protected saveFieldGroupValues(values: ExtractedValuesByKey) {
    this.extractedDataStoreService.storeExtractedBatchFields(this.currentDocumentId(), values);

    if (this.eseAuftragsformValidationActive()) {
      const data = this.extractedDataStoreService.getExtractedBatchFields(this.currentDocumentId());
      const validator = new EseFormValidator(data as ExtractedValuesByKey);

      if (validator.eseAuftragsformValid()) {
        this.pageNavigationService.enableNavigation('continue');
      } else {
        this.pageNavigationService.disableNavigation(
          'continue',
          'Bitte vervollständige bzw. korrigiere deine Eingaben',
        );
      }
    }
  }

  protected saveTableValues(tableKey: string, rows: ExtractedRow[]) {
    this.extractedDataStoreService.storeExtractedRows(this.currentDocumentId(), tableKey, rows);
  }

  protected mergeFormFieldMap(formFieldMap: Record<string, ElementRef>) {
    this.formFieldMap.update((current) => ({ ...current, ...formFieldMap }));
  }

  private scrollToCurrentActiveField(
    currentActiveField: ExtractionField | undefined,
    formFieldMap: Record<string, ElementRef<HTMLElement>>,
  ) {
    if (!currentActiveField) return;
    const currentActiveKey = currentActiveField.model.key;
    formFieldMap[currentActiveKey]?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
}
